/* AuditDialog + AuditHistoryPanel — reusable QA audit components.
 *
 * The QA audit pool (qa_audit.py backend) replaced the old QA gate with
 * an out-of-band sampling model. QA pick any planned case and record:
 *   - decision  : pass | concerns | fail
 *   - score     : 1-5 (optional, used for planner quality trends)
 *   - note      : >=5 chars (required, free text)
 *   - flags     : multi-select from common quality taxonomy + custom
 *
 * The dialog is generic — works on any case that's in an auditable status
 * (sent_for_approval through completed). The history panel lists past
 * audits in reverse chronological order so the case-detail page shows
 * QA's verdicts at a glance.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
    ShieldCheck, ShieldWarning, ShieldSlash, Star, X, Check, Warning,
} from "@phosphor-icons/react";


/* Predefined flag taxonomy. Tech can also type a custom flag in the
   "add another" input — submitted as-is. */
const FLAG_TAXONOMY = [
    { slug: "missing_attachments", label: "Missing attachments" },
    { slug: "ipr_unclear",         label: "IPR markings unclear" },
    { slug: "overcorrection",      label: "Possible over-correction" },
    { slug: "undercorrection",     label: "Possible under-correction" },
    { slug: "scan_quality_low",    label: "Scan quality marginal" },
    { slug: "prescription_mismatch", label: "Plan deviates from Rx" },
    { slug: "staging_concern",     label: "Staging order looks off" },
    { slug: "attachment_placement", label: "Attachment placement off" },
];


const DECISION_META = {
    pass:     { label: "Pass",     desc: "Plan meets quality bar",         tone: "bg-emerald-100 text-emerald-900 border-emerald-300", iconCls: "text-emerald-600", Icon: ShieldCheck },
    concerns: { label: "Concerns", desc: "Acceptable but notes for planner", tone: "bg-amber-100 text-amber-900 border-amber-300",     iconCls: "text-amber-600",   Icon: ShieldWarning },
    fail:     { label: "Fail",     desc: "Plan should be reworked",        tone: "bg-rose-100 text-rose-900 border-rose-300",         iconCls: "text-rose-600",     Icon: ShieldSlash },
};


export function AuditDialog({ open, onClose, caseObj, onSubmitted }) {
    const [decision, setDecision] = useState("pass");
    const [score, setScore] = useState(0);
    const [note, setNote] = useState("");
    const [flags, setFlags] = useState({});
    const [customFlag, setCustomFlag] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setDecision("pass");
            setScore(0);
            setNote("");
            setFlags({});
            setCustomFlag("");
        }
    }, [open]);

    const toggleFlag = (slug) => setFlags((p) => ({ ...p, [slug]: !p[slug] }));
    const selectedFlags = Object.keys(flags).filter((k) => flags[k]);

    const valid = note.trim().length >= 5;

    const submit = async () => {
        if (!valid || busy || !caseObj?.id) return;
        const finalFlags = [...selectedFlags];
        const custom = customFlag.trim();
        if (custom) finalFlags.push(custom);
        setBusy(true);
        try {
            await api.post(`/qa/cases/${caseObj.id}/audit`, {
                decision,
                note: note.trim(),
                score: score || null,
                flags: finalFlags,
            });
            const meta = DECISION_META[decision];
            toast.success(`Audit recorded — ${meta.label}`);
            onSubmitted?.();
            onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to record audit");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="audit-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <ShieldCheck size={18} weight="duotone" className="text-primary" />
                        Record QA audit
                    </DialogTitle>
                    <DialogDescription>
                        {caseObj?.code ? <>For case <span className="font-mono font-semibold">{caseObj.code}</span> — </> : null}
                        Sampling QA is non-blocking. Doctor delivery doesn't pause for your decision.
                    </DialogDescription>
                </DialogHeader>

                {/* Decision radios */}
                <div>
                    <Label className="text-xs font-semibold">Decision</Label>
                    <div className="grid grid-cols-3 gap-2 mt-1">
                        {Object.entries(DECISION_META).map(([slug, m]) => {
                            const active = decision === slug;
                            return (
                                <label
                                    key={slug}
                                    className={`flex flex-col items-center gap-1 p-3 rounded-xl border-2 cursor-pointer transition ${active ? m.tone : "bg-card border-border text-muted-foreground hover:border-primary/30"}`}
                                    data-testid={`audit-decision-${slug}`}
                                >
                                    <input
                                        type="radio"
                                        name="audit-decision"
                                        value={slug}
                                        checked={active}
                                        onChange={() => setDecision(slug)}
                                        className="sr-only"
                                    />
                                    <m.Icon size={20} weight={active ? "fill" : "duotone"} className={m.iconCls} />
                                    <span className="text-xs font-bold uppercase tracking-wider">{m.label}</span>
                                    <span className="text-[10px] text-center leading-tight">{m.desc}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>

                {/* Star score */}
                <div>
                    <Label className="text-xs font-semibold">Quality score (optional)</Label>
                    <div className="flex items-center gap-1 mt-1" data-testid="audit-score">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                onClick={() => setScore(score === n ? 0 : n)}
                                className={`w-9 h-9 rounded-md flex items-center justify-center transition ${
                                    score >= n
                                        ? "bg-amber-100 text-amber-600"
                                        : "bg-secondary text-muted-foreground hover:bg-amber-50 hover:text-amber-500"
                                }`}
                                data-testid={`audit-score-${n}`}
                                title={`${n} star${n === 1 ? "" : "s"}`}
                            >
                                <Star size={16} weight={score >= n ? "fill" : "regular"} />
                            </button>
                        ))}
                        {score > 0 && (
                            <button
                                type="button"
                                onClick={() => setScore(0)}
                                className="ml-1 text-[10px] text-muted-foreground hover:text-foreground"
                            >
                                clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Flag chips */}
                <div>
                    <Label className="text-xs font-semibold">Flags (optional)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1" data-testid="audit-flags">
                        {FLAG_TAXONOMY.map((f) => {
                            const active = !!flags[f.slug];
                            return (
                                <button
                                    key={f.slug}
                                    type="button"
                                    onClick={() => toggleFlag(f.slug)}
                                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold border transition ${
                                        active
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-card border-border text-muted-foreground hover:border-primary/40"
                                    }`}
                                    data-testid={`audit-flag-${f.slug}`}
                                >
                                    {active && <Check size={10} weight="bold" />}
                                    {f.label}
                                </button>
                            );
                        })}
                        <input
                            value={customFlag}
                            onChange={(e) => setCustomFlag(e.target.value)}
                            placeholder="+ custom flag"
                            className="text-[11px] px-2.5 py-1 rounded-full border border-dashed border-border bg-card placeholder:text-muted-foreground w-32 focus:outline-none focus:border-primary"
                            data-testid="audit-flag-custom"
                        />
                    </div>
                </div>

                {/* Note */}
                <div>
                    <Label className="text-xs font-semibold">Note <span className="text-rose-600">*</span></Label>
                    <Textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        rows={3}
                        placeholder={
                            decision === "fail"
                                ? "What needs to be reworked? e.g., 'UR3 attachment placement looks wrong for class II correction'"
                                : decision === "concerns"
                                ? "Notes for the planner. Not blocking but worth recording."
                                : "Anything noteworthy worth recording for the audit log."
                        }
                        data-testid="audit-note"
                    />
                    <p className="text-[10px] text-muted-foreground mt-1">
                        Minimum 5 characters. {note.trim().length < 5 && <span className="text-rose-600 font-semibold">{5 - note.trim().length} more needed.</span>}
                    </p>
                </div>

                <DialogFooter className="mt-2">
                    <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
                    <Button
                        onClick={submit}
                        disabled={!valid || busy}
                        data-testid="audit-submit"
                        className={decision === "fail" ? "bg-rose-600 hover:bg-rose-700 text-white"
                            : decision === "concerns" ? "bg-amber-500 hover:bg-amber-600 text-white"
                            : ""}
                    >
                        {busy ? "Saving…" : `Record ${DECISION_META[decision].label.toLowerCase()}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


/* ─── AuditHistoryPanel — shows all audits on a case, newest first.
   Used inside QaCaseDetail + (eventually) admin/CaseDetail. */
export function AuditHistoryPanel({ caseId, refreshKey }) {
    const [audits, setAudits] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!caseId) return;
        setLoading(true);
        api.get(`/qa/cases/${caseId}/audits`)
            .then((r) => setAudits(r.data || []))
            .catch(() => setAudits([]))
            .finally(() => setLoading(false));
    }, [caseId, refreshKey]);

    if (loading) {
        return <div className="text-xs text-muted-foreground py-2">Loading audit history…</div>;
    }
    if (audits.length === 0) {
        return (
            <div className="text-center py-6 text-muted-foreground text-xs" data-testid="audit-history-empty">
                No QA audits recorded yet on this case.
            </div>
        );
    }
    return (
        <ul className="space-y-2.5" data-testid="audit-history-list">
            {audits.map((a) => (
                <AuditHistoryRow key={a.id} audit={a} />
            ))}
        </ul>
    );
}


function AuditHistoryRow({ audit }) {
    const m = DECISION_META[audit.decision] || DECISION_META.pass;
    const ts = audit.created_at ? new Date(audit.created_at).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
    return (
        <li
            className={`rounded-xl border-2 ${m.tone.replace(/text-\S+/, "")} p-3`}
            data-testid={`audit-history-${audit.id}`}
        >
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <m.Icon size={16} weight="fill" className={m.iconCls} />
                    <span className="text-xs uppercase tracking-wider font-bold">{m.label}</span>
                    {audit.score && (
                        <span className="inline-flex items-center gap-0.5 text-[11px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                            <Star size={10} weight="fill" /> {audit.score}/5
                        </span>
                    )}
                </div>
                <div className="text-[10px] text-muted-foreground">
                    {audit.qa_user_name} · {ts}
                </div>
            </div>
            <p className="text-xs mt-1.5">{audit.note}</p>
            {Array.isArray(audit.flags) && audit.flags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {audit.flags.map((f) => (
                        <span
                            key={f}
                            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-card border border-border text-muted-foreground"
                        >
                            <Warning size={9} weight="bold" /> {String(f).replace(/_/g, " ")}
                        </span>
                    ))}
                </div>
            )}
        </li>
    );
}


export default AuditDialog;
